import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Copy, ClipboardList, Clock3 } from 'lucide-react';

const toDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDateTime = (value) => {
  const d = toDate(value);
  if (!d) return '-';
  return d.toLocaleString('es-MX');
};

const secondsLeft = (executeAt, nowMs) => {
  const date = toDate(executeAt);
  if (!date) return 0;
  return Math.max(0, Math.floor((date.getTime() - nowMs) / 1000));
};

const pad = (n) => String(n).padStart(2, '0');

const formatCountdown = (secs, forceClock = false) => {
  if (secs <= 0) return forceClock ? '00:00:00' : 'ahora';
  const hours = Math.floor(secs / 3600);
  const minutes = Math.floor((secs % 3600) / 60);
  const seconds = secs % 60;

  if (forceClock || secs < 3600) {
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }
  if (secs < 86400) {
    return `${hours}h ${minutes}min`;
  }
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
};

const typeLabel = (type = '') => {
  if (type === 'vendor_notification') return 'Notificación VENDOR';
  if (type === 'lead_notification') return 'Notificación LEAD';
  if (type === 'nurturing') return 'Nurturing';
  return type || 'N/A';
};

export const LeadLogWA = ({ open, onOpenChange, leadId, leadName }) => {
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState({ queue: [], logs: [] });
  const [tabByLog, setTabByLog] = useState({});
  const [nowMs, setNowMs] = useState(Date.now());

  const loadData = async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const r = await api.get(`/whatsapp/nurturing-status/${leadId}`);
      const queue = Array.isArray(r.data?.queue) ? r.data.queue : [];
      const logs = Array.isArray(r.data?.logs) ? r.data.logs : [];
      setPayload({ queue, logs });
      setTabByLog((prev) => {
        const next = { ...prev };
        logs.forEach((l) => {
          if (!next[l.id]) next[l.id] = 'request';
        });
        return next;
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cargar el log de nurturing/WA');
      setPayload({ queue: [], logs: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, leadId]);

  useEffect(() => {
    if (!open) return undefined;
    const timer = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [open]);

  const queue = payload.queue || [];
  const logs = payload.logs || [];
  const pendingSteps = useMemo(
    () => queue.filter((q) => q.status === 'pending').sort((a, b) => (toDate(a.execute_at)?.getTime() || 0) - (toDate(b.execute_at)?.getTime() || 0)),
    [queue]
  );
  const nextPendingId = pendingSteps[0]?.id || '';
  const logicName = queue[0]?.logic_name || '-';

  const pendingWhatsAppItems = useMemo(
    () => queue.filter((q) => q.channel === 'whatsapp' && q.status === 'pending'),
    [queue]
  );

  const copyText = async (text, okMessage) => {
    try {
      await navigator.clipboard.writeText(String(text || ''));
      toast.success(okMessage);
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[96vw] max-w-4xl md:ml-auto md:mr-0 md:h-[100vh] md:max-h-[100vh] md:rounded-none overflow-y-auto bg-[#111822] border-white/10" data-testid="lead-log-wa-drawer">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-emerald-400" />
            Log WA + Nurturing — {leadName || 'Lead'}
          </DialogTitle>
          <DialogDescription className="text-zinc-400">Estado de cola, countdown y detalle request/response de WhatsApp.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex items-center justify-center" data-testid="lead-log-wa-loading">
            <div className="w-7 h-7 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            <section className="rounded-xl border border-white/10 bg-black/20 p-4" data-testid="lead-log-wa-nurturing-section">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm text-white font-semibold">SECUENCIA DE NURTURING</p>
                  <p className="text-xs text-zinc-400 mt-1">Lógica: {logicName}</p>
                </div>
              </div>

              <div className="mt-3 space-y-2">
                {queue.length === 0 ? (
                  <p className="text-xs text-zinc-400" data-testid="lead-log-wa-nurturing-empty">Sin pasos en cola para este lead.</p>
                ) : queue.map((step) => {
                  const isNextPending = step.status === 'pending' && step.id === nextPendingId;
                  const remainSecs = secondsLeft(step.execute_at, nowMs);
                  const icon = step.status === 'completed' ? '✅' : step.status === 'failed' ? '❌' : isNextPending ? '⏳' : '🔲';
                  const accent = step.status === 'completed' ? 'border-emerald-500/20 bg-emerald-500/5' : step.status === 'failed' ? 'border-red-500/20 bg-red-500/5' : isNextPending ? 'border-amber-500/20 bg-amber-500/5' : 'border-white/10 bg-black/20';

                  return (
                    <div key={step.id} className={`rounded-lg border p-3 ${accent}`} data-testid={`lead-log-wa-step-${step.id}`}>
                      <p className="text-sm text-white">
                        {icon} Paso {(Number(step.step_index) || 0) + 1} — {step.channel || 'N/A'} — {step.status}
                      </p>
                      <p className="text-xs text-zinc-300 mt-1">{step.template_name || step.description || '-'}</p>
                      {step.status === 'completed' && <p className="text-[11px] text-zinc-400 mt-1">Enviado: {formatDateTime(step.executed_at || step.execute_at)}</p>}
                      {step.status === 'pending' && (
                        <p className="text-[11px] text-zinc-300 mt-1 flex items-center gap-1">
                          <Clock3 className="w-3 h-3" />
                          Ejecuta en: {formatCountdown(remainSecs, isNextPending)}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="rounded-xl border border-white/10 bg-black/20 p-4" data-testid="lead-log-wa-send-log-section">
              <p className="text-sm text-white font-semibold mb-3">LOG DE ENVÍOS WA</p>

              <div className="space-y-3">
                {logs.length === 0 && pendingWhatsAppItems.length === 0 ? (
                  <p className="text-xs text-zinc-400" data-testid="lead-log-wa-send-empty">Sin envíos ni cola de WhatsApp.</p>
                ) : (
                  <>
                    {logs.map((log) => {
                      const selectedTab = tabByLog[log.id] || 'request';
                      const ok = Number(log.gupshup_status_code) === 200 || Number(log.gupshup_status_code) === 202;
                      return (
                        <div key={log.id} className="rounded-lg border border-white/10 p-3" data-testid={`lead-log-wa-send-item-${log.id}`}>
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <p className="text-sm text-white">📤 {typeLabel(log.type)} — {formatDateTime(log.created_at)}</p>
                            <Badge className={`${ok ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'} border-0`} data-testid={`lead-log-wa-send-http-${log.id}`}>
                              {ok ? '✓' : '✗'} HTTP {log.gupshup_status_code || 'N/A'}
                            </Badge>
                          </div>
                          <p className="text-xs text-zinc-300 mt-1">Tipo: {log.type || 'N/A'} · Plantilla: {log.template_name || 'N/A'}</p>

                          <div className="flex gap-2 mt-3">
                            <button type="button" className={`text-xs px-2 py-1 rounded border ${selectedTab === 'request' ? 'border-emerald-500/30 text-emerald-300' : 'border-white/10 text-zinc-400'}`} onClick={() => setTabByLog((p) => ({ ...p, [log.id]: 'request' }))} data-testid={`lead-log-wa-tab-request-${log.id}`}>REQUEST (cURL)</button>
                            <button type="button" className={`text-xs px-2 py-1 rounded border ${selectedTab === 'response' ? 'border-cyan-500/30 text-cyan-300' : 'border-white/10 text-zinc-400'}`} onClick={() => setTabByLog((p) => ({ ...p, [log.id]: 'response' }))} data-testid={`lead-log-wa-tab-response-${log.id}`}>RESPONSE</button>
                          </div>

                          {selectedTab === 'request' ? (
                            <>
                              <pre className="mt-2 text-[11px] bg-black/40 border border-white/10 rounded-md p-2 overflow-x-auto text-zinc-200" data-testid={`lead-log-wa-request-${log.id}`}>{log.curl_equivalent || '-'}</pre>
                              <Button size="sm" variant="outline" className="mt-2" onClick={() => copyText(log.curl_equivalent, 'cURL copiado')} data-testid={`lead-log-wa-copy-request-${log.id}`}>
                                <Copy className="w-3.5 h-3.5 mr-1" /> Copiar cURL
                              </Button>
                            </>
                          ) : (
                            <>
                              <pre className="mt-2 text-[11px] bg-black/40 border border-white/10 rounded-md p-2 overflow-x-auto text-zinc-200" data-testid={`lead-log-wa-response-${log.id}`}>{JSON.stringify(log.gupshup_response || {}, null, 2)}</pre>
                              <Button size="sm" variant="outline" className="mt-2" onClick={() => copyText(JSON.stringify(log.gupshup_response || {}, null, 2), 'Respuesta copiada')} data-testid={`lead-log-wa-copy-response-${log.id}`}>
                                <Copy className="w-3.5 h-3.5 mr-1" /> Copiar respuesta
                              </Button>
                            </>
                          )}
                        </div>
                      );
                    })}

                    {pendingWhatsAppItems.map((item) => {
                      const remain = secondsLeft(item.execute_at, nowMs);
                      return (
                        <div key={`pending-${item.id}`} className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3" data-testid={`lead-log-wa-pending-item-${item.id}`}>
                          <p className="text-sm text-white">📤 Nurturing Paso {(Number(item.step_index) || 0) + 1} — pendiente</p>
                          <p className="text-xs text-zinc-300 mt-1">Tipo: nurturing · Plantilla: {item.template_name || item.description || 'N/A'}</p>
                          <p className="text-xs text-amber-300 mt-1">Estado: ⏳ En cola — ejecuta en {formatCountdown(remain, item.id === nextPendingId)}</p>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
